import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Radio } from '@3plearning/react-components';
import Isvg from 'react-inlinesvg';
import { ModalBody, Header, Content, Preview, InputWrapper, Footer, Type, InfoContent } from './styled';
import { PreviewDemoIcon, Info } from '../../helpers/icon-dictionary';
import Checkbox from '../checkbox';
import {
  CHECKED,
  UNCHECKED
} from '../checkbox/constants';
import Schedule from '../schedule';
import { durationTypes } from '../../helpers/utils';
import ContextModal from '../context-modal';
import usePreview from '../../helpers/hooks/use-preview';
import { getAllLearningJourneys } from '../../containers/upf-assign/upf-assign-util';

function UpfAssignModal({ closeAssignModal, onAssignHandler, assignInfo, i18n }) {
  const [randomize, setCheckboxState] = useState(UNCHECKED);
  const [duration, setDuration] = useState(0);
  const [scheduleDateTime, setScheduleDateTime] = useState([null, null]);
  const [scheduleBlockAssign, setScheduleBlockAssign] = useState(false);
  const [isScheduleOn, setIsScheduleOn] = useState(false);
  const [durationType, setDurationType] = useState(null);
  const [showContext, setShowContext] = useState(false);
  const [contextPosition, setContextPostion] = useState({});
  const infoRef = useRef(null);
  const modalRef = useRef(null);
  const assignmentDurationRef = useRef(null);
  const questionDurationRef = useRef(null);
  const infoContextWidth = 375;
  const { assessmentType = '' } = assignInfo || {};
  const selectedTopics = useSelector(state => state.UPFAssignReducer.selectedTopics);
  const [isLoading, handlePreview] = usePreview(getAllLearningJourneys(selectedTopics));

  const handleCheckboxClick = (cbStatus) => {
    setCheckboxState(cbStatus);
  };

  const handleDurationTypeChange = (type) => {
    if (type !== durationType) {
      setDurationType(type);
      setDuration(0);
      if (type === durationTypes.PER_ASSIGNMENT) {
        questionDurationRef.current.value = '';
      } else {
        assignmentDurationRef.current.value = '';
      }
    }
  };

  const handleInputChange = (e) => {
    const isValidInt = /^\d+$/.test(e.target.value);
    setDuration(isValidInt ? parseInt(e.target.value, 10) : 0);
  };

  const handleSchedule = (scheduleProps) => {
    const { dateRange, blockAssign, isOn } = scheduleProps;
    setScheduleDateTime(dateRange);
    setScheduleBlockAssign(blockAssign);
    setIsScheduleOn(isOn);
  };

  const onPreviewHandler = () => {
    handlePreview(
      durationType === durationTypes.PER_ASSIGNMENT ? duration * 60 : duration,
      durationType,
      randomize === CHECKED
    );
  };

  const getContextPos = () => {
    const arrowHeight = 10;
    const { left: modalX, top: modalY } = modalRef.current.parentElement.getBoundingClientRect();
    const iconElement = infoRef.current.querySelector('.isvg');
    const { left: posX, top: posY, width, height } = iconElement.getBoundingClientRect();
    const posArrow = (posX + (width / 2)) - modalX;
    setContextPostion({
      posX: posArrow - (infoContextWidth / 2),
      posY: (posY + height + (arrowHeight / 2)) - modalY,
      posArrow
    });
  };

  return (
    <ModalBody ref={modalRef}>
      <Header>{i18n.t`Assessment Settings`}</Header>
      <Type>
        <div className="heading">{i18n.t`Type`}</div>
        <div className="subHeading">{assessmentType}</div>
      </Type>
      <Content>
        <div className="heading duration">{i18n.t`Duration`}
          <span className="asterix">*</span>
        </div>
        <div className="assessmentDetails">
          <div className="detailsCont">
            <Radio
              selected={durationType === durationTypes.PER_ASSIGNMENT}
              onSelect={() => {
                handleDurationTypeChange(durationTypes.PER_ASSIGNMENT);
              }}
              label={
                <div className="duration-length">
                  <div>{i18n.t`Set assessment length`}</div>
                  <InputWrapper
                    type="number"
                    onChange={handleInputChange}
                    ref={assignmentDurationRef}
                  />
                  {i18n.t`mins`}
                </div>
              }
              className="duration-option"
              name="duration"
              value={durationTypes.PER_ASSIGNMENT}
            />
          </div>
          <div className="detailsCont">
            <Radio
              selected={durationType === durationTypes.PER_QUESTION}
              onSelect={() => {
                handleDurationTypeChange(durationTypes.PER_QUESTION);
              }}
              label={
                <div className="duration-length">
                  <div>{i18n.t`Set question length`}</div>
                  <div className="duration-field">
                    <InputWrapper
                      type="number"
                      onChange={handleInputChange}
                      ref={questionDurationRef}
                    />
                    <span>{i18n.t`sec`}</span>
                    <span
                      ref={infoRef}
                      className="info-icon"
                      onMouseEnter={() => { setShowContext(true); }}
                      onMouseLeave={() => { setShowContext(false); }}
                    >
                      <Isvg src={Info} cacheGetRequests onLoad={getContextPos} />
                    </span>
                  </div>
                </div>
              }
              className="duration-option"
              name="duration"
              value={durationTypes.PER_QUESTION}
            />
            <ContextModal
              show={showContext}
              positionArrow={contextPosition.posArrow}
              positionX={contextPosition.posX}
              positionY={contextPosition.posY}
              fixedParent
              modalWidth={infoContextWidth}
            >
              <InfoContent width={infoContextWidth} onMouseLeave={() => { setShowContext(false); }}>
                <p>{i18n.t`Setting the question length will set a time limit on each of the questions.`}</p>
                <p>
                  {i18n.t`Responses entered by students will be submitted automatically at the end of the time limit.`}
                </p>
                <p>
                  {i18n.t`Student screens will remain on the current question and will be automatically
                  progressed to the next question once the time limit is up.`}
                </p>
                <p>
                  {i18n.t`This setting is useful for basic facts assessments,
                  and any other assessments that would benefit from controlled question time limits.`}
                </p>
              </InfoContent>
            </ContextModal>
          </div>
        </div>
      </Content>
      <Content>
        <div className="randomize">
          <Checkbox
            checkboxHandler={cbStatus => handleCheckboxClick(cbStatus)}
            state={randomize}
            name="randomize">
            <span className="text">{i18n.t`Create random sequence per student`}</span>
          </Checkbox>
        </div>
      </Content>
      <Schedule
        i18n={i18n}
        scheduleCallBack={handleSchedule}
      />
      <Preview>
        <Button
          alignIcon="LEFT"
          className="previewButton"
          icon={PreviewDemoIcon}
          disabled={isLoading || duration <= 0}
          onClick={onPreviewHandler}
          primary
          text={i18n.t`Preview Assessment`}
          type={Button.LOZENGE} />
      </Preview>
      <Footer>
        <div className="ctrlButtons">
          <Button
            alignIcon="LEFT"
            className="btn cancelBtn"
            onClick={closeAssignModal}
            primary
            text={i18n.t`Cancel`}
            type={Button.LOZENGE} />
          <Button
            alignIcon="RIGHT"
            className="btn assignBtn"
            onClick={() => onAssignHandler(
              durationType === durationTypes.PER_ASSIGNMENT ? duration * 60 : duration,
              randomize === CHECKED,
              scheduleDateTime,
              durationType
            )}
            primary
            disabled={duration <= 0 || scheduleBlockAssign}
            text={isScheduleOn ? i18n.t`Assign` : i18n.t`Assign now`}
            type={Button.LOZENGE} />
        </div>
      </Footer>
    </ModalBody>
  );
}

export function createAssignModal(onAssignHandler, closeAssignModal, assignInfo, i18n) {
  return {
    content: <UpfAssignModal
      onAssignHandler={onAssignHandler}
      closeAssignModal={closeAssignModal}
      assignInfo={assignInfo}
      i18n={i18n} />,
    id: 0,
    className: 'assignModalStyle',
    buttons: []
  };
}

UpfAssignModal.propTypes = {
  assignInfo: PropTypes.object,
  i18n: PropTypes.object,
  closeAssignModal: PropTypes.func,
  onAssignHandler: PropTypes.func
};

export default UpfAssignModal;

